import { KTSVG } from '_metronic/helpers';
import { VJFPUser } from 'app/API';
import { AppTopNav } from 'app/modules/shared/AppTopNav';
import { BoothService } from 'app/services/BoothService';
import { JobfairService } from 'app/services/JobfairService';
import { QueueUserService } from 'app/services/QueueUserService';
import { VJFPUserService } from 'app/services/VJFPUserService';
import { BasePageProps, Roles } from 'app/types';
import React, { useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';

export const AdminUsers: React.FC<BasePageProps> = (props: BasePageProps) => {
  const [searchEmail, setSearchEmail] = useState('');
  const [user, setUser] = useState<VJFPUser | null | undefined>(undefined);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const vjfpUserService = new VJFPUserService();

  const handleSearch = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    setSearched(false);
    setUser(undefined);
    setError(null);
    setSuccessMsg(null);
    try {
      const result = await vjfpUserService.getByEmail(searchEmail.trim());
      setUser(result || null);
    } catch {
      setUser(null);
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  const isRecruiter = (u: VJFPUser) =>
    u.roles?.includes(Roles.Recruiter) || u.roles?.includes(Roles.EmployerAdmin);
  const isJobSeeker = (u: VJFPUser) =>
    u.roles?.includes(Roles.Applicant);

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    setError(null);
    setSuccessMsg(null);
    try {
      if (isRecruiter(user)) {
        await removeRecruiterFromAllBooths(user);
      }
      if (isJobSeeker(user)) {
        await removeJobSeekerFromQueuesAndRegistrations(user);
      }
      await vjfpUserService.deleteUserById(user.organizationId, user.id);
      setSuccessMsg(`User ${user.emailId} has been removed successfully.`);
      setUser(undefined);
      setSearchEmail('');
      setSearched(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const removeRecruiterFromAllBooths = async (u: VJFPUser) => {
    const jobFairService = new JobfairService();
    const boothService = new BoothService();
    const jobFairs = await jobFairService.getJobFairs(u.organizationId);
    for (const jf of jobFairs) {
      const booths = await boothService.getAllBooths(jf.id);
      for (const booth of booths) {
        if (booth.recruiters?.includes(u.id)) {
          await boothService.updateBooth(jf.id, booth.id, {
            recruiters: booth.recruiters.filter(r => r !== u.id).map(r => r || null)
          });
        }
      }
    }
  };

  const removeJobSeekerFromQueuesAndRegistrations = async (u: VJFPUser) => {
    const jobFairService = new JobfairService();
    const queueUserService = new QueueUserService();

    const orgId = '00000000-0000-0000-0000-000000000000';
    const jobFairs = await jobFairService.getJobFairs(orgId);
    for (const jf of jobFairs) {
      await queueUserService.leaveQueueFromAllBooths(jf.id, u.id);
    }
  };

  return (
    <div className='chatPage'>
      <AppTopNav title={props.title} breadcrumbs={props.breadcrumbs} />
      <div className='container-fluid mt-10'>
        <div className='row mb-5'>
          <div className='col'>
            <div className='card card-custom shadow-sm'>
              <div className='card-header border-0 pt-6'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bold fs-3 mb-1'>Search User</span>
                </h3>
              </div>
              <div className='card-body pt-0'>
                <div className='d-flex gap-3 mb-5'>
                  <Form.Control
                    type='email'
                    placeholder='Enter email address to search...'
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className='w-300px'
                  />
                  <Button
                    variant='primary'
                    onClick={handleSearch}
                    disabled={searching || !searchEmail.trim()}
                  >
                    {searching
                      ? (
                      <>
                        <Spinner animation='border' size='sm' className='me-2' />
                        Searching...
                      </>
                        )
                      : (
                      <>
                        <KTSVG
                          path='/media/icons/duotone/General/Search.svg'
                          className='svg-icon-3 me-2'
                        />
                        Search
                      </>
                        )}
                  </Button>
                </div>

                {error && <Alert variant='danger'>{error}</Alert>}
                {successMsg && <Alert variant='success'>{successMsg}</Alert>}

                {searched && user === null && (
                  <div className='text-center py-8'>
                    <KTSVG
                      path='/media/icons/duotone/General/Search.svg'
                      className='svg-icon-5x svg-icon-muted mb-4'
                    />
                    <div className='text-muted fw-bold fs-4'>No user found with this email</div>
                  </div>
                )}

                {user && (
                  <div className='border rounded p-4'>
                    <div className='d-flex align-items-center justify-content-between mb-4'>
                      <div className='d-flex align-items-center gap-4'>
                        <div className='symbol symbol-50px'>
                          <div className='symbol-label bg-light-primary fs-4 fw-bold text-primary'>
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </div>
                        </div>
                        <div>
                          <div className='text-gray-800 fw-bold fs-4'>
                            {user.firstName} {user.lastName}
                          </div>
                          <div className='text-muted fs-6'>{user.emailId}</div>
                        </div>
                      </div>
                      <div className='d-flex gap-2'>
                        {user.roles?.map((role) => (
                          <Badge key={role} bg='primary' className='px-3 py-2 fs-7'>
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className='d-flex flex-column gap-2 fs-6'>
                      <div><span className='text-muted'>ID:</span> <span className='fw-bold ms-2'>{user.id}</span></div>
                      <div><span className='text-muted'>Organization:</span> <span className='fw-bold ms-2'>{user.organizationId}</span></div>
                      {user.phone_number && (
                        <div><span className='text-muted'>Phone:</span> <span className='fw-bold ms-2'>{user.phone_number}</span></div>
                      )}
                    </div>

                    <div className='separator my-4'></div>

                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='danger'
                        onClick={() => setShowConfirm(true)}
                        disabled={deleting}
                      >
                        {deleting
                          ? (
                          <>
                            <Spinner animation='border' size='sm' className='me-2' />
                            Removing...
                          </>
                            )
                          : (
                          <>
                            <KTSVG
                              path='/media/icons/duotone/General/Trash.svg'
                              className='svg-icon-3 me-2'
                            />
                            Remove User
                          </>
                            )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Modal show={showConfirm} onHide={() => !deleting && setShowConfirm(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm User Removal</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {user && (
              <div>
                <p className='mb-3'>
                  Are you sure you want to remove <strong>{user.firstName} {user.lastName}</strong> ({user.emailId}) from the platform?
                </p>
                {isRecruiter(user) && (
                  <div className='bg-warning bg-opacity-10 border border-warning rounded p-3 mb-3'>
                    <strong>Recruiter:</strong> They will be unassigned from all booths before removal.
                  </div>
                )}
                {isJobSeeker(user) && (
                  <div className='bg-warning bg-opacity-10 border border-warning rounded p-3 mb-3'>
                    <strong>Job Seeker:</strong> They will be removed from all queues and registrations before removal.
                  </div>
                )}
                <p className='text-danger mb-0'>This action cannot be undone. Their name will appear as &ldquo;Deleted User&rdquo; in history and reports.</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowConfirm(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant='danger' onClick={handleDelete} disabled={deleting}>
              {deleting
                ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Removing...
                </>
                  )
                : (
                    'Confirm Remove'
                  )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};
